import { Dialog } from 'primereact/dialog'
import { ProgressSpinner } from 'primereact/progressspinner'
import { FC, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { getRunGeometryImages, ReportGeometryImage } from '../../../api/report-generator-API'
import GeometryProxyImage from './GeometryProxyImage'
import { SectionEmpty } from './Section'

export type GeometryImagesProps = {
  /** Proposed run whose geometry-change images are shown. */
  runName?: string
}

/** Geometry-change image grid with a click-to-enlarge lightbox (CFD report §5). */
const GeometryImages: FC<GeometryImagesProps> = ({ runName }) => {
  const [images, setImages] = useState<ReportGeometryImage[]>([])
  const [loading, setLoading] = useState(false)
  const [lightbox, setLightbox] = useState<ReportGeometryImage | null>(null)

  useEffect(() => {
    if (!runName) {
      setImages([])
      return
    }
    let cancelled = false
    setLoading(true)
    getRunGeometryImages(runName)
      .then(imgs => {
        if (!cancelled) setImages(imgs)
      })
      .catch(() => {
        if (!cancelled) {
          setImages([])
          toast.error('Failed to load geometry images.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [runName])

  if (!runName) return <SectionEmpty />

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-6 justify-center">
        <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" /> Loading geometry
        images…
      </div>
    )
  }

  if (!images.length) {
    return <SectionEmpty message="No geometry-change images are attached to this run." />
  }

  return (
    <>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
        {images.map(img => (
          <figure
            key={img.blobPath ?? img.name}
            className="border border-gray-200 dark:border-neutral-70 rounded overflow-hidden bg-gray-50 dark:bg-neutral-80">
            <button
              type="button"
              className="block w-full aspect-[4/3] !p-0 !bg-transparent cursor-zoom-in"
              onClick={() => setLightbox(img)}>
              <GeometryProxyImage blobPath={img.blobPath} gmid={img.gmid} alt={img.name} />
            </button>
            <figcaption className="text-[10px] text-gray-500 px-1.5 py-1 truncate" title={img.name}>
              {img.name}
            </figcaption>
          </figure>
        ))}
      </div>

      <Dialog
        visible={!!lightbox}
        onHide={() => setLightbox(null)}
        header={lightbox?.name}
        dismissableMask
        style={{ width: '90vw', maxWidth: '1100px' }}>
        {lightbox && (
          <div className="w-full h-[70vh] flex items-center justify-center bg-neutral-100 dark:bg-neutral-90">
            <GeometryProxyImage
              blobPath={lightbox.blobPath}
              gmid={lightbox.gmid}
              alt={lightbox.name}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
      </Dialog>
    </>
  )
}

export default GeometryImages
